<script setup lang="ts">
import { useUIStore } from '@/stores/uiStore'
import { isMenuDivider } from '@/lib/types/UI/Menus'

/**
 * Responsive sidebar — two modes:
 *
 *   Mobile (`<md`): fixed overlay drawer that slides in from the
 *     left when `ui.sidebarMobileOpen` is true. A backdrop scrim
 *     (in `default.vue`) sits BEHIND the drawer and dismisses on
 *     tap. `sidebarMobileOpen` auto-clears on route change (see
 *     `uiStore`) so clicking a nav item dismisses the drawer
 *     naturally.
 *
 *   Desktop (`md+`): in-flow flex child with two widths:
 *     - expanded  `md:w-60` — full labels alongside icons
 *     - compact   `md:w-16` — icons only (label visually hidden)
 *     Toggle via the chevron decorator straddling the divider in
 *     `default.vue` → `ui.toggleSidebarCompact()`.
 *
 * Width is the load-bearing animation channel here — `w-0` / `w-60`
 * / `w-16` transition smoothly via Tailwind's `duration-normal` on
 * the same `ease-decel` token used for the editor drawer push.
 *
 * The mobile drawer uses `translate-x-*` rather than `w-0` so the
 * sidebar's CONTENTS don't reflow during the slide — they're laid
 * out at the drawer's full mobile width (`w-64`) and shifted via
 * transform, which is GPU-composited and won't trigger layout.
 */
const uiStore = useUIStore()
</script>

<template>
  <aside
    :aria-hidden="!uiStore.sidebarMobileOpen && undefined"
    :class="[
      // ── Positioning ─────────────────────────────────────────────
      // Mobile: ABSOLUTE overlay bounded by the layout's body row
      // (the `<div .flex .relative>` in `default.vue`) — header
      // stays visible above, footer below, drawer slides in WITHIN
      // the main content region. `fixed` would have it overlay the
      // entire viewport which blocked header + footer.
      // Desktop: relative, in-flow flex child of that same row.
      'absolute inset-y-0 left-0 z-40 md:relative md:inset-auto md:z-auto',
      // ── Visual chrome ────────────────────────────────────────────
      'border-r border-border overflow-hidden bg-canvas',
      // Slide + width animate together. `duration-normal` is the
      // theme token used for layout-tier transitions.
      'transition-all duration-normal ease-decel',
      // Don't shrink past intrinsic width in the desktop flex row.
      'flex-shrink-0',
      // ── Width ────────────────────────────────────────────────────
      // Mobile width is fixed at `w-64` (visible drawer); on desktop
      // it switches to 60 (expanded) or 16 (compact). The mobile
      // value stays out of the desktop cascade via the `md:` prefixes.
      'w-64',
      uiStore.sidebarCompact ? 'md:w-16' : 'md:w-60',
      // ── Translate (mobile slide) ────────────────────────────────
      // On `md+` we force `translate-x-0` regardless of mobile state
      // so the desktop sidebar isn't accidentally hidden off-canvas
      // when `sidebarMobileOpen` is false.
      uiStore.sidebarMobileOpen ? 'translate-x-0' : '-translate-x-full',
      'md:translate-x-0',
    ]"
  >
    <nav class="py-4">
      <ul class="flex flex-col gap-1 px-2">
        <template v-for="(item, idx) in uiStore.menuItems" :key="isMenuDivider(item) ? `__div_${idx}` : item.href">
          <!-- Divider entry: render a thin horizontal rule with breathing room. -->
          <li v-if="isMenuDivider(item)" class="my-2 mx-3 border-t border-border" aria-hidden="true" />
          <!-- Standard menu item. -->
          <li v-else>
            <RouterLink
              v-tooltip.right="uiStore.sidebarCompact ? { value: item.name, hideDelay: 0 } : undefined"
              :to="{ name: item.href }"
              class="relative flex items-center gap-3 px-3 py-1.5 rounded-md text-sm font-normal text-secondary hover:bg-surface-hover hover:text-primary"
              active-class="!bg-accent-soft !text-[var(--theme-accent-text)] font-semibold before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-accent before:content-['']"
              :class="[
                // In compact mode, center the single icon — labels
                // collapse out via `hidden`. On mobile + desktop-
                // expanded, keep the icon+label row.
                uiStore.sidebarCompact ? 'md:justify-center md:px-2' : '',
              ]"
            >
              <i v-if="item.icon" :class="['pi', item.icon, 'text-base flex-shrink-0']" />
              <span
                class="truncate"
                :class="uiStore.sidebarCompact ? 'md:hidden' : ''"
              >{{ item.name }}</span>
              <!-- Optional chip (e.g. "Demo") — violet tone-1, hidden in compact. -->
              <span
                v-if="item.chip"
                class="ml-auto rounded px-1.5 py-px text-[9px] font-bold uppercase leading-none tracking-wide"
                :class="uiStore.sidebarCompact ? 'md:hidden' : ''"
                :style="{
                  backgroundColor: 'color-mix(in srgb, var(--theme-tone-1) 16%, transparent)',
                  color: 'var(--theme-tone-1)',
                }"
              >{{ item.chip }}</span>
            </RouterLink>

            <!-- Expandable sub-items — shown when the parent route is
                 active (not in compact). Children share the parent route,
                 so active-state is query-driven (`child.current` from
                 uiStore), NOT RouterLink's path match. -->
            <ul
              v-if="item.children?.length && item.current && !uiStore.sidebarCompact"
              class="mb-1 ml-5 mt-1 flex flex-col gap-0.5 border-l border-border pl-2"
            >
              <li v-for="child in item.children" :key="`${child.href}_${child.query?.view ?? ''}`">
                <RouterLink
                  :to="{ name: child.href, query: child.query }"
                  class="flex items-center gap-2 rounded-md px-3 py-1 text-[13px] text-secondary hover:bg-surface-hover hover:text-primary"
                  :class="
                    child.current ? '!bg-accent-soft !text-[var(--theme-accent-text)] font-semibold' : ''
                  "
                >
                  <span class="truncate">{{ child.name }}</span>
                </RouterLink>
              </li>
            </ul>
          </li>
        </template>
      </ul>
    </nav>
  </aside>
</template>
